import { create } from 'zustand';
import type { AudienceQfaQuestion } from '@mentimeter/http-clients';
import { getAudienceQuestions } from '../../api/qfa';

const INITIAL_PAGE_SIZE = 30;

// To prevent spam
const SUBMIT_TRIES = 3;
const SUBMIT_DELAY = 30 * 1000;
const PUNISHMENT = 60 * 1 * 1000;

export interface QfaState {
  error: string | null;
  loading: boolean;
  hasNextPage?: boolean;
  pageSize: number;
  questions: AudienceQfaQuestion[] | undefined;
  shouldDoLiveUpdate: boolean;
  sortByUpvote: boolean;
  userQuestions: number[];
  isQfaPanelOpen: boolean;

  // Spam
  punishment: number;
  submissions: number;
  submitTimestamp: number;

  // Actions
  updateUpvotes: (id: number, upvotes: number) => void;
  updateUpvote: (id: number, isUpvoted: boolean) => void;
  setSortByUpvote: (sort: boolean) => void;
  openQfaPanel: () => void;
  closeQfaPanel: () => void;
  setLoading: (loading: boolean) => void;
  /**
   * Sets the audience question and updates the state.
   * Includes handling of spam prevention.
   * @param question - The audience question to set.
   */
  setAudienceQuestion: (question: AudienceQfaQuestion) => void;
  fetchAudienceQuestions: (voteKey: string) => Promise<void>;
  fetchNextAudienceQuestionsPage: (voteKey: string) => Promise<void>;
  resetError: () => void;
  setError: (error: string) => void;
}

export const useQfaStore = create<QfaState>((set, get) => ({
  error: null,
  loading: false,
  hasNextPage: false,
  pageSize: INITIAL_PAGE_SIZE,
  questions: undefined,
  shouldDoLiveUpdate: true,
  sortByUpvote: false,
  userQuestions: [],
  isQfaPanelOpen: false,
  punishment: 0,
  submissions: 0,
  submitTimestamp: 0,

  updateUpvotes: (id, upvotes) =>
    set((state) => ({
      questions: state.questions?.map((q) =>
        q.id === id ? { ...q, upvotes } : q,
      ),
    })),

  updateUpvote: (id, isUpvoted) =>
    set((state) => ({
      questions: state.questions?.map((q) => {
        if (q.id !== id) return q;

        const currentUpvotes = q.upvotes ?? 0;
        const voteChange = isUpvoted ? 1 : -1;
        return { ...q, upvotes: Math.max(0, currentUpvotes + voteChange) };
      }),
    })),

  setSortByUpvote: (sort) => set({ sortByUpvote: sort }),
  openQfaPanel: () => set({ isQfaPanelOpen: true }),
  closeQfaPanel: () => set({ isQfaPanelOpen: false }),
  resetError: () => set({ error: null }),
  setError: (error: string) => set({ error }),
  setLoading: (loading: boolean) => set({ loading }),

  setAudienceQuestion: (question: AudienceQfaQuestion) => {
    set((state) => {
      const currentTime = Date.now();
      let punishment = 0;
      let submissions = 0;

      if (state.punishment === 0 || state.punishment < currentTime) {
        const diff = currentTime - state.submitTimestamp;
        const shouldReset = state.submitTimestamp !== 0 && diff > SUBMIT_DELAY;
        const isSpamming =
          diff <= SUBMIT_DELAY && state.submissions >= SUBMIT_TRIES - 1;

        punishment = isSpamming ? currentTime + PUNISHMENT : 0;
        submissions = shouldReset ? 1 : state.submissions + 1;
      }

      return {
        questions: [...(state.questions || []), question],
        userQuestions: [...state.userQuestions, question.id],
        loading: false,
        punishment,
        submissions,
        submitTimestamp:
          currentTime - state.submitTimestamp > SUBMIT_DELAY
            ? currentTime
            : state.submitTimestamp,
      };
    });
  },

  fetchAudienceQuestions: async (voteKey) => {
    set({ error: null, loading: true });

    try {
      const { data } = await getAudienceQuestions(voteKey, get().sortByUpvote);
      set({
        questions: data.data,
        pageSize: data.page_size || get().pageSize,
        hasNextPage: !!data.next_page,
        error: null,
        loading: false,
        shouldDoLiveUpdate: true,
      });
    } catch (err: any) {
      set({ error: err.message, loading: false });
    }
  },

  fetchNextAudienceQuestionsPage: async (voteKey) => {
    set({ error: null, loading: true });
    const { pageSize, questions = [] } = get();
    const page = Math.ceil(questions.length / pageSize + 1);

    try {
      const { data } = await getAudienceQuestions(voteKey, false, page);

      // Filter out the profanities when filter is on
      const questionsWithoutProfanities = data.data.filter(
        (question) => !question.is_profanity,
      );

      set((state) => ({
        questions: state.questions
          ? [...state.questions, ...questionsWithoutProfanities]
          : questionsWithoutProfanities,
        hasNextPage: !!data.next_page,
        error: null,
        loading: false,
        shouldDoLiveUpdate: false,
      }));
    } catch (err: any) {
      set({ error: err.message, loading: false });
    }
  },
}));
